import '../styles/modals.css'
import '../styles/viewer.css' // reused for .custom-color-picker
import React, { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import { Spinner } from 'react-bootstrap'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import {
  getAreas,
  getTopics,
  getIndicators,
  getCountries,
} from '../actions/searchActions'
import { roundy } from './Utilities'
import { buildOrderedIndicatorGroups, getUnitsInOrder, sortUnitsForDisplay } from '../utils/seriesOptions'
import { getTransformationLabel } from '../utils/transformations'
import { DISPLAY_MODE_OPTIONS, PERIOD_FORMAT_OPTIONS } from '../utils/indicatorFormatting'

const DEFAULT_CARD_BACKGROUND_COLOR = '#ffffff'

const DASHBOARD_SELECT_STYLES = {
  menuPortal: base => ({ ...base, zIndex: 2000 }),
}

const DASHBOARD_SELECT_PROPS = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
  styles: DASHBOARD_SELECT_STYLES,
}

const normalizeOptionSource = (value) => (Array.isArray(value) ? value : [])
const NO_TRANSFORMATION_OPTION = { value: '', label: 'None' }

const normalizeTransformation = (value) => String(value || '').trim()
const getTransformationOption = value => {
  const normalized = normalizeTransformation(value?.value ?? value)
  return normalized
    ? { value: normalized, label: getTransformationLabel(normalized) }
    : NO_TRANSFORMATION_OPTION
}

const filterRowsByMetric = (rows, unit, transformation = '') => {
  const requestedTransformation = normalizeTransformation(transformation)
  return (rows || []).filter(row => (
    row.unit === unit &&
    normalizeTransformation(row.transformation) === requestedTransformation
  ))
}

function getLatestValue(records) {
  const sorted = [...records]
    .filter(item => item.value !== null && item.value !== undefined && !Number.isNaN(Number(item.value)))
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))

  return sorted[sorted.length - 1] || null
}

function getMeanValue(records, fromDate, toDate) {
  if (!fromDate || !toDate) return null

  const start = String(fromDate) <= String(toDate) ? String(fromDate) : String(toDate)
  const end = String(fromDate) <= String(toDate) ? String(toDate) : String(fromDate)
  const values = records
    .filter(item => String(item.date) >= start && String(item.date) <= end)
    .map(item => Number(item.value))
    .filter(value => !Number.isNaN(value))

  if (values.length === 0) return null
  return values.reduce((sum, value) => sum + value, 0) / values.length
}

function EditWidgetIndicator({ closeCallback, updateCallback, data = {}, wid }) {
  const dispatch = useDispatch()
  const hasExistingIndicator = Object.keys(data || {}).length > 0

  const [countryValue, setCountryValue] = useState(data.countryValue || null)
  const [indicatorValue, setIndicatorValue] = useState(data.indicatorValue || null)
  const [frequencyValue, setFrequencyValue] = useState(data.frequencyValue || null)
  const [unitValue, setUnitValue] = useState(data.unitValue || null)
  const [transformationValue, setTransformationValue] = useState(
    getTransformationOption(data.transformationValue)
  )
  const [timeframeValue, setTimeframeValue] = useState(data.timeframeValue || null)
  const [timeframe2Value, setTimeframe2Value] = useState(data.timeframe2Value || null)
  const [rangeValue, setRangeValue] = useState(data.rangeValue || false)
  const [titleValue, setTitleValue] = useState(data.titleValue || data.indicatorValue?.label || '')
  const [subtitleValue, setSubtitleValue] = useState(data.subtitleValue || '')
  const [unitSuffixValue, setUnitSuffixValue] = useState(data.unitSuffixValue || '')
  const [unitLabelValue, setUnitLabelValue] = useState(data.unitLabelValue || '')
  // Indicators created before this toggle existed always showed "(Country)"
  // appended to the unit label, so `undefined` must keep behaving as shown —
  // only an explicit `false` (saved from this checkbox) turns it off.
  const [showCountryInUnitValue, setShowCountryInUnitValue] = useState(data.showCountryInUnitLabel !== false)
  const [backgroundColorValue, setBackgroundColorValue] = useState(data.backgroundColor || '#ffffff')
  const [displayModeValue, setDisplayModeValue] = useState(
    DISPLAY_MODE_OPTIONS.find(option => option.value === data.displayModeValue) || DISPLAY_MODE_OPTIONS[0]
  )
  const [periodFormatValue, setPeriodFormatValue] = useState(
    PERIOD_FORMAT_OPTIONS.find(option => option.value === data.periodFormatValue) || PERIOD_FORMAT_OPTIONS[0]
  )
  const [frequencyOptions, setFrequencyOptions] = useState([])
  const [unitOptions, setUnitOptions] = useState([])
  const [transformationOptions, setTransformationOptions] = useState([NO_TRANSFORMATION_OPTION])
  const [timeframeOptions, setTimeframeOptions] = useState([])
  const [loadedSeriesItem, setLoadedSeriesItem] = useState(null)
  const [availableIndicatorIds, setAvailableIndicatorIds] = useState(null)
  const [fallbackCountries, setFallbackCountries] = useState([])
  const [loadingFallbackCountries, setLoadingFallbackCountries] = useState(false)
  const [loadingAvailability, setLoadingAvailability] = useState(false)
  const [loadingSeriesOptions, setLoadingSeriesOptions] = useState(false)
  const [loadingAdd, setLoadingAdd] = useState(false)
  const [modalError, setModalError] = useState('')

  useEffect(() => {
    dispatch(getAreas())
    dispatch(getTopics())
    dispatch(getIndicators())
    dispatch(getCountries())
  }, [])

  const { areaList = [] } = useSelector(state => state.areaList)
  const { topicList = [] } = useSelector(state => state.topicList)
  const { loading: loadingIndicator, indicatorList = [] } = useSelector(state => state.indicatorList)
  const { loading: loadingCountry, countryList = [] } = useSelector(state => state.countryList)

  useEffect(() => {
    let cancelled = false

    const fetchCountriesFallback = async () => {
      setLoadingFallbackCountries(true)
      try {
        const { data } = await axios.get('/api/get-countries/')
        if (!cancelled) setFallbackCountries(normalizeOptionSource(data))
      } catch (error) {
        if (!cancelled) setModalError('Countries could not be loaded. Try refreshing the page.')
      } finally {
        if (!cancelled) setLoadingFallbackCountries(false)
      }
    }

    fetchCountriesFallback()

    return () => {
      cancelled = true
    }
  }, [])

  const countrySource = useMemo(() => {
    const reduxCountries = normalizeOptionSource(countryList)
    return reduxCountries.length > 0 ? reduxCountries : fallbackCountries
  }, [countryList, fallbackCountries])

  const countryOptions = useMemo(() => (
    countrySource
      .filter(item => item?.name)
      .map(item => ({
        value: item.id || item.name,
        label: item.name,
      }))
  ), [countrySource])

  const isCountrySelectLoading = countryOptions.length === 0 && (loadingCountry || loadingFallbackCountries)

  const indicatorOptions = useMemo(() => (
    buildOrderedIndicatorGroups(indicatorList, topicList, areaList, availableIndicatorIds)
  ), [areaList, availableIndicatorIds, indicatorList, topicList])

  useEffect(() => {
    if (!hasExistingIndicator) return

    const countryName = data.countryValue?.label
    const indicatorName = data.indicatorValue?.api || data.indicatorValue?.label
    const topicId = data.indicatorValue?.topic || data.topicValue?.value
    const topicName = data.indicatorValue?.topicApi || topicList.find(item => item.id === topicId)?.name || ''

    if (countryName && !countryValue) {
      const country = countryOptions.find(item => item.label === countryName) || data.countryValue
      setCountryValue(country)
    }

    if (indicatorName && !indicatorValue) {
      const indicator = indicatorList.find(item => item.name === indicatorName && (!topicId || item.foreign_topic === topicId))
      setIndicatorValue({
        value: indicator?.id || data.indicatorValue?.value || indicatorName,
        label: indicatorName,
        area: indicator?.foreign_area || data.indicatorValue?.area,
        topic: indicator?.foreign_topic || topicId,
        topicApi: topicName,
        api: indicatorName,
      })
    }
  }, [countryOptions, countryValue, data, hasExistingIndicator, indicatorList, indicatorValue, topicList])

  useEffect(() => {
    const fetchAvailability = async () => {
      if (!hasExistingIndicator) {
        setIndicatorValue(null)
        setFrequencyValue(null)
        setUnitValue(null)
        setTransformationValue(NO_TRANSFORMATION_OPTION)
        setTimeframeValue(null)
        setTimeframe2Value(null)
        setFrequencyOptions([])
        setUnitOptions([])
        setTransformationOptions([NO_TRANSFORMATION_OPTION])
        setTimeframeOptions([])
        setLoadedSeriesItem(null)
      }
      setAvailableIndicatorIds(null)
      setModalError('')

      if (!countryValue) return

      setLoadingAvailability(true)
      try {
        const { data } = await axios.post('/api/v1/data/indicators-for-countries/', { countries: [countryValue.label] })
        setAvailableIndicatorIds(new Set((data.indicators || []).filter(item => item.has_any_data).map(item => item.indicator_id)))
      } catch (error) {
        setModalError('Available indicators could not be loaded for this country.')
      } finally {
        setLoadingAvailability(false)
      }
    }

    fetchAvailability()
  }, [countryValue, hasExistingIndicator])

  useEffect(() => {
    const fetchSeriesOptions = async () => {
      setLoadedSeriesItem(null)
      setModalError('')

      if (!countryValue || !indicatorValue) return

      setLoadingSeriesOptions(true)
      try {
        const requestedFrequency = frequencyValue?.value || data.frequencyValue?.value || 'Annual'
        const { data: response } = await axios.post('/api/v1/data/request/', {
          topic: indicatorValue.topicApi ? [indicatorValue.topicApi] : [],
          indicator: [indicatorValue.api],
          country: [countryValue.label],
          frequency: requestedFrequency,
        }, { timeout: 30000 })

        const item = response?.data_list?.[0]
        if (!item?.group || !Array.isArray(item.data) || item.data.length === 0) {
          setUnitOptions([])
          setTimeframeOptions([])
          setModalError('No data was found for this country and indicator.')
          return
        }

        setLoadedSeriesItem(item)

        const availableFrequencies = item.group.available_frequencies?.length
          ? item.group.available_frequencies
          : [item.group.frequency || requestedFrequency]
        setFrequencyOptions(availableFrequencies.map(value => ({ value, label: value })))
        if (!frequencyValue || !availableFrequencies.includes(frequencyValue.value)) {
          const nextFrequency = availableFrequencies.includes(requestedFrequency) ? requestedFrequency : availableFrequencies[0]
          setFrequencyValue({ value: nextFrequency, label: nextFrequency })
        }

        const unitsFromData = getUnitsInOrder(item.data)
        const units = unitsFromData.length > 0
          ? unitsFromData
          : sortUnitsForDisplay(item.group.units)
        setUnitOptions(units.map(value => ({ value, label: value })))
        if (!unitValue || !units.includes(unitValue.value)) {
          const requestedUnit = data.unitValue?.value
          const nextUnit = requestedUnit && units.includes(requestedUnit) ? requestedUnit : units[0]
          setUnitValue(nextUnit ? { value: nextUnit, label: nextUnit } : null)
        }
      } catch (error) {
        setModalError(error.code === 'ECONNABORTED'
          ? 'The indicator options took too long to load. Try another indicator or try again.'
          : 'Frequency and metric options could not be loaded.'
        )
      } finally {
        setLoadingSeriesOptions(false)
      }
    }

    fetchSeriesOptions()
  }, [countryValue, data.frequencyValue, data.unitValue, frequencyValue?.value, indicatorValue])

  useEffect(() => {
    if (!loadedSeriesItem || !unitValue) {
      setTimeframeOptions([])
      setTransformationOptions([NO_TRANSFORMATION_OPTION])
      return
    }

    const transformedForUnit = [...new Set((loadedSeriesItem.data || [])
      .filter(item => item.unit === unitValue.value)
      .map(item => normalizeTransformation(item.transformation))
      .filter(Boolean))]
      .sort()

    const nextTransformationOptions = [
      NO_TRANSFORMATION_OPTION,
      ...transformedForUnit.map(value => ({
        value,
        label: getTransformationLabel(value),
      })),
    ]
    setTransformationOptions(nextTransformationOptions)

    const requestedTransformation = data.transformationValue?.value || ''
    const currentTransformationOption = nextTransformationOptions.find(option => option.value === transformationValue.value)
    const currentTransformationExists = Boolean(currentTransformationOption)
    if (!currentTransformationOption) {
      const nextTransformation = nextTransformationOptions.find(option => option.value === requestedTransformation) || NO_TRANSFORMATION_OPTION
      setTransformationValue(nextTransformation)
    } else if (currentTransformationOption.label !== transformationValue.label) {
      setTransformationValue(currentTransformationOption)
    }

    const selectedRows = filterRowsByMetric(loadedSeriesItem.data, unitValue.value, currentTransformationExists ? transformationValue.value : requestedTransformation)

    const dates = [...new Set(selectedRows
      .map(item => item.date)
      .filter(Boolean))]
      .sort()

    const nextOptions = dates.map(value => ({ value, label: value }))
    setTimeframeOptions(nextOptions)

    const currentDateExists = timeframeValue && dates.includes(timeframeValue.value)
    if (!currentDateExists) {
      const requestedDate = data.timeframeValue?.value
      const nextDate = requestedDate && dates.includes(requestedDate) ? requestedDate : dates[dates.length - 1]
      setTimeframeValue(nextDate ? { value: nextDate, label: nextDate } : null)
    }

    const currentDate2Exists = timeframe2Value && dates.includes(timeframe2Value.value)
    if (rangeValue && !currentDate2Exists) {
      const requestedDate2 = data.timeframe2Value?.value
      const nextDate2 = requestedDate2 && dates.includes(requestedDate2) ? requestedDate2 : dates[0]
      setTimeframe2Value(nextDate2 ? { value: nextDate2, label: nextDate2 } : null)
    }
  }, [data.timeframe2Value, data.timeframeValue, data.transformationValue, loadedSeriesItem, rangeValue, timeframe2Value, timeframeValue, transformationValue, unitValue])

  const sendHandler = async () => {
    if (!countryValue || !indicatorValue || !frequencyValue || !unitValue || !timeframeValue || (rangeValue && !timeframe2Value) || loadingAdd || loadingSeriesOptions) return

    setLoadingAdd(true)
    setModalError('')
    try {
      let item = loadedSeriesItem
      if (!item || item.group?.frequency !== frequencyValue.value) {
        const { data: response } = await axios.post('/api/v1/data/request/', {
          topic: indicatorValue.topicApi ? [indicatorValue.topicApi] : [],
          indicator: [indicatorValue.api],
          country: [countryValue.label],
          frequency: frequencyValue.value,
        }, { timeout: 30000 })
        item = response?.data_list?.[0]
      }

      if (!item?.group || !Array.isArray(item.data) || item.data.length === 0) {
        setModalError('No data was found for this country, indicator, frequency, and metric.')
        return
      }

      const filteredData = filterRowsByMetric(item.data, unitValue.value, transformationValue.value)
      const selectedValue = rangeValue
        ? getMeanValue(filteredData, timeframeValue.value, timeframe2Value.value)
        : getLatestValue(filteredData.filter(row => row.date === timeframeValue.value))?.value

      if (selectedValue === null || selectedValue === undefined || Number.isNaN(Number(selectedValue))) {
        setModalError('No valid value was found for the selected period.')
        return
      }

      const content = {
        backgroundColor: backgroundColorValue,
        areaValue: {
          value: indicatorValue.area,
          label: areaList.find(item => item.id === indicatorValue.area)?.beauty_name || '',
        },
        topicValue: {
          value: indicatorValue.topic,
          label: topicList.find(item => item.id === indicatorValue.topic)?.beauty_name || '',
        },
        indicatorValue,
        unitValue,
        transformationValue,
        frequencyValue,
        countryValue,
        timeframeValue,
        timeframe2Value: rangeValue ? timeframe2Value : null,
        rangeValue,
        cardValue: roundy(Number(selectedValue), 2),
        titleValue: titleValue.trim(),
        subtitleValue: subtitleValue.trim(),
        unitSuffixValue: unitSuffixValue.trim(),
        unitLabelValue: unitLabelValue.trim(),
        showCountryInUnitLabel: showCountryInUnitValue,
        displayModeValue: displayModeValue.value,
        periodFormatValue: periodFormatValue.value,
      }

      updateCallback(wid, content)
      closeCallback(true)
    } catch (error) {
      setModalError(error.code === 'ECONNABORTED'
        ? 'The indicator request took too long. Try another indicator or try again.'
        : 'The indicator could not be loaded. Try again or choose another indicator.'
      )
    } finally {
      setLoadingAdd(false)
    }
  }

  return (
    <div className='modal-container'>
      <div className='modal-window dashboard-series-modal px-5 pb-5 pt-4 d-flex flex-column align-items-center'>
        <i onClick={() => closeCallback(false)} style={{ cursor: 'pointer' }} className="fa-solid fa-xmark text-secondary fs-4 ms-auto"></i>
        <h1 style={{ color: '#313A52' }} className='bold-title fs-1'>{hasExistingIndicator ? 'Edit Indicator' : 'Add Indicator'}</h1>

        <p className='p-paragraph text-center'>
          Select a country, indicator, frequency, metric, and period for the dashboard value.
        </p>

        <span className='p-label-2 w-100'>Title:</span>
        <input
          className='dashboard-series-title-input'
          type='text'
          value={titleValue}
          onChange={event => setTitleValue(event.target.value)}
          placeholder='Use the default title (indicator name) or write a custom one'
        />

        <span className='p-label-2 w-100'>Subtitle:</span>
        <input
          className='dashboard-series-title-input'
          type='text'
          value={subtitleValue}
          onChange={event => setSubtitleValue(event.target.value)}
          placeholder='Optional subtitle'
        />

        <span className='p-label-2 w-100'>Unit label:</span>
        <input
          className='dashboard-series-title-input'
          type='text'
          value={unitLabelValue}
          onChange={event => setUnitLabelValue(event.target.value)}
          placeholder={unitValue?.label ? `Uses "${unitValue.label}" by default` : 'Uses the selected metric by default'}
        />

        <div className='d-flex align-items-center w-100' style={{ gap: '0.5em' }}>
          <input
            id='show-country-in-unit-label'
            type='checkbox'
            checked={showCountryInUnitValue}
            onChange={event => setShowCountryInUnitValue(event.target.checked)}
          />
          <label htmlFor='show-country-in-unit-label' className='p-label-2' style={{ cursor: 'pointer' }}>
            Show country in unit label{countryValue?.label ? ` (${countryValue.label})` : ''}
          </label>
        </div>

        <div className='d-flex align-items-center w-100 my-2' style={{ gap: '0.5em' }}>
          <span className='p-label-2'>Card background:</span>
          <input value={backgroundColorValue} onChange={event => setBackgroundColorValue(event.target.value)} className='custom-color-picker' type='color' />
          {backgroundColorValue !== DEFAULT_CARD_BACKGROUND_COLOR && (
            <i
              onClick={() => setBackgroundColorValue(DEFAULT_CARD_BACKGROUND_COLOR)}
              style={{ cursor: 'pointer', color: '#808080' }}
              className="fa-solid fa-arrow-rotate-left"
              title='Reset to default background'
            ></i>
          )}
        </div>

        <span className='p-label-2 w-100'>Select Country:</span>
        <Select
          className={`custom-select-container ${isCountrySelectLoading && 'button-disabled'}`}
          classNamePrefix="search-select"
          options={countryOptions}
          placeholder={isCountrySelectLoading ? 'Loading countries...' : 'Select country'}
          value={countryValue}
          onChange={option => {
            setCountryValue(option)
            setIndicatorValue(null)
            setFrequencyValue(null)
            setUnitValue(null)
            setTransformationValue(NO_TRANSFORMATION_OPTION)
            setTimeframeValue(null)
            setTimeframe2Value(null)
            setFrequencyOptions([])
            setUnitOptions([])
            setTransformationOptions([NO_TRANSFORMATION_OPTION])
            setTimeframeOptions([])
            setLoadedSeriesItem(null)
          }}
          {...DASHBOARD_SELECT_PROPS}
        />

        {loadingAvailability && (
          <div className='dashboard-series-helper'>Loading available indicators...</div>
        )}

        <span className='p-label-2 text-start w-100 pt-3'>Select Indicator:</span>
        <Select
          className={`custom-select-container ${(!countryValue || loadingIndicator || loadingAvailability) && 'button-disabled'}`}
          classNamePrefix="search-select"
          options={indicatorOptions}
          placeholder={countryValue ? 'Search available indicators' : 'Select country first'}
          value={indicatorValue}
          onChange={option => {
            setIndicatorValue(option)
            if (option?.label && !titleValue.trim()) setTitleValue(option.label)
            setFrequencyValue(null)
            setUnitValue(null)
            setTransformationValue(NO_TRANSFORMATION_OPTION)
            setTimeframeValue(null)
            setTimeframe2Value(null)
            setFrequencyOptions([])
            setUnitOptions([])
            setTransformationOptions([NO_TRANSFORMATION_OPTION])
            setTimeframeOptions([])
            setLoadedSeriesItem(null)
          }}
          {...DASHBOARD_SELECT_PROPS}
        />

        {loadingSeriesOptions && (
          <div className='dashboard-series-helper'>Loading frequency, metric, and period options...</div>
        )}

        <div className='dashboard-series-grid'>
          <div>
            <span className='p-label-2 text-start w-100 pt-3'>Select Frequency:</span>
            <Select
              className={`custom-select-container ${(!indicatorValue || loadingSeriesOptions) && 'button-disabled'}`}
              classNamePrefix="search-select"
              options={frequencyOptions}
              placeholder='Select frequency'
              value={frequencyValue}
              onChange={option => {
                setFrequencyValue(option)
                setUnitValue(null)
                setTransformationValue(NO_TRANSFORMATION_OPTION)
                setTimeframeValue(null)
                setTimeframe2Value(null)
                setUnitOptions([])
                setTransformationOptions([NO_TRANSFORMATION_OPTION])
                setTimeframeOptions([])
                setLoadedSeriesItem(null)
              }}
              {...DASHBOARD_SELECT_PROPS}
            />
          </div>

          <div>
            <span className='p-label-2 text-start w-100 pt-3'>Select Metric:</span>
            <Select
              className={`custom-select-container ${(!frequencyValue || loadingSeriesOptions) && 'button-disabled'}`}
              classNamePrefix="search-select"
              options={unitOptions}
              placeholder='Select metric'
              value={unitValue}
              onChange={option => {
                setUnitValue(option)
                setTransformationValue(NO_TRANSFORMATION_OPTION)
                setTimeframeValue(null)
                setTimeframe2Value(null)
              }}
              {...DASHBOARD_SELECT_PROPS}
            />
          </div>
        </div>

        {transformationOptions.length > 1 && (
          <div className='dashboard-series-grid'>
            <div>
              <span className='p-label-2 text-start w-100 pt-3'>Select Variation:</span>
              <Select
                className={`custom-select-container ${(!unitValue || loadingSeriesOptions) && 'button-disabled'}`}
                classNamePrefix="search-select"
                options={transformationOptions}
                placeholder='Select variation'
                value={transformationValue}
                onChange={option => {
                  setTransformationValue(option || NO_TRANSFORMATION_OPTION)
                  setTimeframeValue(null)
                  setTimeframe2Value(null)
                }}
                {...DASHBOARD_SELECT_PROPS}
              />
            </div>
          </div>
        )}

        <div className='dashboard-series-range-row'>
          <label>
            <input
              type='checkbox'
              checked={rangeValue}
              onChange={event => {
                setRangeValue(event.target.checked)
                if (!event.target.checked) setTimeframe2Value(null)
              }}
            />
            Use range average
          </label>
        </div>

        <div className='dashboard-series-grid'>
          <div>
            <span className='p-label-2 text-start w-100 pt-3'>{rangeValue ? 'From:' : 'Select Period:'}</span>
            <Select
              className={`custom-select-container ${(!unitValue || loadingSeriesOptions) && 'button-disabled'}`}
              classNamePrefix="search-select"
              options={timeframeOptions}
              placeholder='Select period'
              value={timeframeValue}
              onChange={option => setTimeframeValue(option)}
              {...DASHBOARD_SELECT_PROPS}
            />
          </div>

          {rangeValue && (
            <div>
              <span className='p-label-2 text-start w-100 pt-3'>To:</span>
              <Select
                className={`custom-select-container ${(!unitValue || loadingSeriesOptions) && 'button-disabled'}`}
                classNamePrefix="search-select"
                options={timeframeOptions}
                placeholder='Select period'
                value={timeframe2Value}
                onChange={option => setTimeframe2Value(option)}
                {...DASHBOARD_SELECT_PROPS}
              />
            </div>
          )}
        </div>

        <div className='dashboard-series-grid'>
          <div>
            <span className='p-label-2 text-start w-100 pt-3'>Value display:</span>
            <Select
              className='custom-select-container'
              classNamePrefix="search-select"
              options={DISPLAY_MODE_OPTIONS}
              value={displayModeValue}
              onChange={option => setDisplayModeValue(option || DISPLAY_MODE_OPTIONS[0])}
              {...DASHBOARD_SELECT_PROPS}
            />
          </div>

          <div>
            <span className='p-label-2 text-start w-100 pt-3'>Unit next to value:</span>
            <input
              className='dashboard-series-title-input'
              type='text'
              value={unitSuffixValue}
              onChange={event => setUnitSuffixValue(event.target.value)}
              placeholder='Optional, e.g. %, USD, kg'
            />
          </div>

          {frequencyValue?.value === 'Quarterly' && (
            <div>
              <span className='p-label-2 text-start w-100 pt-3'>Period label:</span>
              <Select
                className='custom-select-container'
                classNamePrefix="search-select"
                options={PERIOD_FORMAT_OPTIONS}
                value={periodFormatValue}
                onChange={option => setPeriodFormatValue(option || PERIOD_FORMAT_OPTIONS[0])}
                {...DASHBOARD_SELECT_PROPS}
              />
            </div>
          )}
        </div>

        {modalError && (
          <div className='dashboard-series-warning'>{modalError}</div>
        )}

        <div onClick={sendHandler} style={{ width: '100%' }} className={`login-button ${(!countryValue || !indicatorValue || !frequencyValue || !unitValue || !timeframeValue || (rangeValue && !timeframe2Value) || loadingAdd || loadingSeriesOptions) ? 'button-disabled' : ''} mt-3`}>
          {loadingAdd ? (
            <Spinner animation="border" variant='light' as='span' style={{ width: '20px', height: '20px' }} />
          ) : (
            hasExistingIndicator ? 'Update Indicator' : 'Add Indicator'
          )}
        </div>
      </div>
    </div>
  )
}

export default EditWidgetIndicator
